"use client";

import {
  FloatingFocusManager,
  FloatingPortal,
  flip,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import clsx from "clsx";
import { FC, PropsWithChildren, useState } from "react";
import { PopoverProps } from "./types";

const Popover: FC<PropsWithChildren<PopoverProps>> = ({
  children,
  content,
  variant = "primary",
  trigger = "click",
  padding = true,
  isOpen,
  dismissOnOutsidePress = true,
  placement,
  onOpenChange,
}) => {
  const isControlled = typeof isOpen === "boolean";
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const internalIsOpen = isControlled ? isOpen : isPopoverOpen;
  const handleOpenChange = isControlled ? onOpenChange : setIsPopoverOpen;

  const { refs, floatingStyles, context } = useFloating({
    placement,
    open: internalIsOpen,
    onOpenChange: handleOpenChange,
    middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  const click = useClick(context, {
    ignoreMouse: trigger !== "click",
    keyboardHandlers: true,
  });
  const hover = useHover(context, {
    enabled: trigger === "hover",
    handleClose: safePolygon(),
  });
  const role = useRole(context);

  const dismiss = useDismiss(context, {
    outsidePress: dismissOnOutsidePress,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    role,
    hover,
    dismiss,
  ]);

  const variants = {
    primary: "bg-primary-800 text-neutral-0 shadow-3",
    white: "bg-neutral-0 text-neutral-675 shadow-3",
  };

  return (
    <span className="block h-fit w-fit">
      {internalIsOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              style={floatingStyles}
              ref={refs.setFloating}
              className={clsx(
                "w-max rounded-s max-w-[calc(100%-16px)] m:max-w-[360px] focus:outline-none z-[52] text-body-2",
                variants[variant],
                {
                  "px-2s py-3xs": padding,
                },
              )}
              {...getFloatingProps()}
            >
              {content}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}

      <div
        tabIndex={0}
        role="button"
        ref={refs.setReference}
        {...getReferenceProps()}
        className="h-fit w-fit block"
      >
        {children}
      </div>
    </span>
  );
};

export default Popover;
